import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { X } from "@phosphor-icons/react";

const StoreCTAContext = createContext({ open: () => {} });

export const useStoreCTA = () => useContext(StoreCTAContext);

export const ComingSoonProvider = ({ children }) => {
  const [render, setRender] = useState(false); // mounted in DOM
  const [show, setShow] = useState(false); // animation state
  const okRef = useRef(null);
  const lastFocused = useRef(null);

  const open = useCallback(() => {
    lastFocused.current = document.activeElement;
    setRender(true);
  }, []);

  const close = useCallback(() => {
    setShow(false);
    window.setTimeout(() => {
      setRender(false);
      if (lastFocused.current && lastFocused.current.focus) {
        lastFocused.current.focus();
      }
    }, 200);
  }, []);

  // trigger enter animation + focus + body scroll lock once mounted
  useEffect(() => {
    if (!render) return;
    const raf = requestAnimationFrame(() => setShow(true));
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = window.setTimeout(() => okRef.current && okRef.current.focus(), 60);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
      document.body.style.overflow = prevOverflow;
    };
  }, [render]);

  // ESC to close + rudimentary focus trap (single focusable: OK button)
  useEffect(() => {
    if (!render) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      } else if (e.key === "Tab") {
        e.preventDefault();
        okRef.current && okRef.current.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [render, close]);

  return (
    <StoreCTAContext.Provider value={{ open }}>
      {children}
      {render && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="coming-soon-title"
          aria-describedby="coming-soon-desc"
          data-testid="coming-soon-modal"
        >
          <div
            onClick={close}
            data-testid="coming-soon-backdrop"
            className="absolute inset-0 bg-[#02040a]/80 backdrop-blur-sm transition-opacity duration-200"
            style={{ opacity: show ? 1 : 0 }}
            aria-hidden="true"
          />
          <div
            className="glass-strong relative w-full max-w-md overflow-hidden rounded-3xl p-8 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] transition-all duration-200"
            style={{
              opacity: show ? 1 : 0,
              transform: show ? "scale(1) translateY(0)" : "scale(0.95) translateY(8px)",
            }}
          >
            <div
              className="pointer-events-none absolute -top-24 left-1/2 h-56 w-80 -translate-x-1/2 rounded-full bg-teal-500/15 blur-[90px]"
              aria-hidden="true"
            />
            <button
              onClick={close}
              aria-label="Close dialog"
              data-testid="coming-soon-close"
              className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-xl border border-white/10 text-white/60 transition-colors hover:border-teal-500/40 hover:text-teal-400"
            >
              <X size={18} />
            </button>

            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full border border-teal-500/25 bg-teal-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-teal-300">
                Coming Soon
              </span>
              <h2
                id="coming-soon-title"
                className="mt-5 font-display text-2xl font-bold tracking-tight text-white"
              >
                Velora Intelligence is coming soon
              </h2>
              <p
                id="coming-soon-desc"
                className="mt-4 text-sm leading-relaxed text-white/60"
              >
                The mobile app is currently in final preparation for release on
                the Apple App Store and Google Play. We'll replace these buttons
                with the official store links as soon as the apps are approved.
              </p>
              <button
                ref={okRef}
                onClick={close}
                data-testid="coming-soon-ok"
                className="mt-7 w-full rounded-full bg-teal-500 px-6 py-3 text-sm font-semibold text-midnight outline-none transition-transform duration-300 hover:scale-[1.02] hover:bg-teal-400 focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a101d]"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </StoreCTAContext.Provider>
  );
};
